/**
 * Visualization Process
 * 
 * Handles visualization signals from Cortex API and renders them as views.
 * Each visualization signal spawns its own view instance.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { VisualizationView } from './visualization-view.js';
import { validateFull } from './validation.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import type { VisualizationSignal } from '../../types/visualization.js';

/**
 * Visualization Process
 * 
 * Listens for visualization signals and creates views for each signal.
 * Supports multiple simultaneous visualizations.
 */
export class VisualizationProcess extends BaseProcess {
  kind: string = 'visualization';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'system' as const,
    canStop: true,
    preserveState: false,
    timeSensitive: false
  };

  // Map of visualization ID to view instance
  private visualizationViews: Map<string, VisualizationView> = new Map();
  
  // Debounce/throttle timers for different priority levels
  private renderQueue: Map<string, {
    signal: VisualizationSignal;
    priority: 'critical' | 'supplementary' | 'reflective';
    timer?: number;
  }> = new Map();

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {

    // Track if we've already processed a signal ID to prevent duplicates
    const processedSignalIds = new Set<string>();

    // Listen for visualization signals on event bus
    this.onEvent('visualization.signal', (payload: unknown) => {
      
      // Check for duplicate processing
      if (payload && typeof payload === 'object') {
        const signal = payload as { id?: string };
        if (signal.id) {
          if (processedSignalIds.has(signal.id)) {
            return;
          }
          processedSignalIds.add(signal.id);
        }
      }
      
      this.handleVisualizationSignal(ctx, payload);
    });

    // Also listen for alternative event names (for compatibility)
    // BUT: Check if we've already processed this signal to prevent duplicates
    this.onEvent('ui:visualization', (payload: unknown) => {
      
      // Check for duplicate processing (same signal might come through both events)
      if (payload && typeof payload === 'object') {
        const signal = payload as { id?: string };
        if (signal.id) {
          if (processedSignalIds.has(signal.id)) {
            return;
          }
          processedSignalIds.add(signal.id);
        }
      }
      
      this.handleVisualizationSignal(ctx, payload);
    });

    // Listen for visualization updates (same ID, new data)
    this.onEvent('visualization.update', (payload: unknown) => {
      this.handleVisualizationUpdate(ctx, payload);
    });

    // Listen for visualization removal
    this.onEvent('visualization.remove', (payload: unknown) => {
      this.handleVisualizationRemove(ctx, payload);
    });

    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  /**
   * Handle incoming visualization signal
   */
  private handleVisualizationSignal(ctx: ProcessContext, payload: unknown): void {
    
    // Validate payload
    if (!payload || typeof payload !== 'object') {
      return;
    }

    // Validate signal structure
    const validation = validateFull(payload);
    if (!validation.valid) {
      return;
    }

    const signal = payload as VisualizationSignal;
    
    // Check if visualization already exists (update instead of create)
    // IMPORTANT: This prevents duplicate views from being created
    if (this.visualizationViews.has(signal.id)) {
      this.handleVisualizationUpdate(ctx, payload);
      return;
    }

    // Determine priority
    const priority = this.determinePriority(signal);
    
    // Handle based on priority
    if (priority === 'critical') {
      // Render immediately
      this.createVisualizationView(ctx, signal);
    } else if (priority === 'supplementary') {
      // Debounce 500ms
      this.queueVisualization(signal, priority, 500);
    } else if (priority === 'reflective') {
      // Throttle per iteration
      this.queueVisualization(signal, priority, 1000);
    } else {
      // Diagnostic - skip in production (could add flag to show in dev mode)
      return;
    }
  }

  /**
   * Handle visualization update (same ID, new data)
   */
  private handleVisualizationUpdate(ctx: ProcessContext, payload: unknown): void {
    if (!payload || typeof payload !== 'object') {
      return;
    }

    const validation = validateFull(payload);
    if (!validation.valid) {
      return;
    }

    const signal = payload as VisualizationSignal;
    const view = this.visualizationViews.get(signal.id);
    
    if (view) {
      // Update existing view
      view.updateSignal(signal);
    } else {
      // Create new view if it doesn't exist
      this.createVisualizationView(ctx, signal);
    }
  }

  /**
   * Handle visualization removal
   */
  private handleVisualizationRemove(ctx: ProcessContext, payload: unknown): void {
    if (!payload || typeof payload !== 'object') {
      return;
    }

    const data = payload as { id?: string };
    if (!data.id) {
      return;
    }

    this.removeVisualizationView(ctx, data.id);
  }

  /**
   * Create a visualization view
   */
  private createVisualizationView(ctx: ProcessContext, signal: VisualizationSignal): void {
      
    try {
      // Create view
      const view = new VisualizationView(
        this.id,
        `viz-${signal.id}`,
        signal
      );

      // Convert to ViewInstance and register
      const viewInstance = view.toViewInstance();
      ctx.registerView(viewInstance);
      
      // Store reference
      this.visualizationViews.set(signal.id, view);
      
    } catch (error) {
    }
  }

  /**
   * Remove a visualization view
   */
  private removeVisualizationView(ctx: ProcessContext, visualizationId: string): void {
    const view = this.visualizationViews.get(visualizationId);
    if (view) {
      // Destroy view
      ctx.destroyView?.(`viz-${visualizationId}`);
      this.visualizationViews.delete(visualizationId);
      
      // Clear any queued render for this visualization
      this.clearQueuedVisualization(visualizationId);
      
    }
  }

  /**
   * Determine visualization priority
   */
  private determinePriority(signal: VisualizationSignal): 'critical' | 'supplementary' | 'reflective' | 'diagnostic' {
    // Check meta priority
    if (signal.meta?.priority !== undefined) {
      if (signal.meta.priority >= 0.8) return 'critical';
      if (signal.meta.priority >= 0.5) return 'supplementary';
      if (signal.meta.priority >= 0.2) return 'reflective';
      return 'diagnostic';
    }

    // Check if ephemeral (ephemeral = supplementary by default)
    if (signal.ephemeral === true) {
      return 'supplementary';
    }

    // Check source cortex (some cortices are more critical)
    const criticalCortices = ['decision', 'integrator', 'empirical'];
    if (signal.sourceCortex && criticalCortices.includes(signal.sourceCortex.toLowerCase())) {
      return 'critical';
    }

    // Default to supplementary
    return 'supplementary';
  }

  /**
   * Queue visualization for debounced/throttled rendering
   */
  private queueVisualization(
    signal: VisualizationSignal,
    priority: 'supplementary' | 'reflective',
    delay: number
  ): void {
    // Clear existing timer if any
    this.clearQueuedVisualization(signal.id);

    // Set new timer
    const timer = window.setTimeout(() => {
      // Get context (stored in BaseProcess)
      const context = this.context;
      if (context) {
        this.createVisualizationView(context, signal);
      } else {
      }
      this.renderQueue.delete(signal.id);
    }, delay);

    // Store in queue
    this.renderQueue.set(signal.id, {
      signal,
      priority,
      timer
    });
  }

  /**
   * Clear queued visualization
   */
  private clearQueuedVisualization(visualizationId: string): void {
    const queued = this.renderQueue.get(visualizationId);
    if (queued?.timer) {
      clearTimeout(queued.timer);
    }
    this.renderQueue.delete(visualizationId);
  }

  protected onStop(): void {

    // Clear all queued visualizations
    for (const [id] of this.renderQueue) {
      this.clearQueuedVisualization(id);
    }
    this.renderQueue.clear();

    // Clear all views (they will be cleaned up by view-manager)
    this.visualizationViews.clear();
  }
}

// Register the process
registerProcess('visualization', (id: string) => new VisualizationProcess(id));

